import React, { useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';

const primaryColor = '#77bd43';
const accentColor = '#efa00f';

type Props = {
  value?: number;
  pain?: boolean;
  onChange: (val: { value?: number; pain?: boolean }) => void;
  min: number;
  max: number;
  left: string;
  right: string;
  step: number;
  preferred?: number;
  valueSet: boolean;
  grayColor?: string;
  showTopLabels?: boolean;
  disabled?: boolean;
};

export const Slider: React.FC<Props> = ({
  value,
  pain,
  onChange,
  min,
  max,
  left,
  right,
  step = 1,
  preferred,
  valueSet,
  grayColor = '#e5e7eb',
  showTopLabels = false,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [clickStartedValue, setClickStartedValue] = useState<number | null>(null);
  const rangeId = `slider-${v4()?.replace(/-/g, '')}`;
  const indicatorColor = useMemo(
    () => (valueSet ? (value === preferred ? primaryColor : accentColor) : grayColor),
    [valueSet, value, preferred, grayColor]
  );

  useEffect(() => {
    const sheetToBeRemoved = document.getElementById('slider-styles');
    const sheetParent = sheetToBeRemoved?.parentNode;

    if (sheetParent) {
      sheetParent.removeChild(sheetToBeRemoved);
    }

    const sheet = document.createElement('style');
    sheet.setAttribute('id', `slider-styles-${rangeId}`);
    sheet.innerHTML = `
    .${rangeId} {
      width: 100%;
      margin-top: 10px;
      -ms-touch-action: pan-y;
      touch-action: pan-y;
      line-height: 36px;
      font-size: 24px;
    }
    .${rangeId}__range {
      -webkit-appearance: none;
      width: 100%;
      height: 10px;
      background: ${grayColor};
      outline: none;
      padding: 0;
      margin: 0;
      border-radius: 10px;
    }
    .${rangeId}__range::-webkit-slider-thumb {
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: ${indicatorColor};
      cursor: pointer;
      transition: background 0.15s ease-in-out;
    }
    .${rangeId}__range::-webkit-slider-thumb:hover {
      background: ${indicatorColor};
    }
    .${rangeId}__range:active::-webkit-slider-thumb {
      background: ${indicatorColor};
    }
    .${rangeId}__range::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border: 0;
      border-radius: 50%;
      background: ${indicatorColor};
      cursor: pointer;
      transition: background 0.15s ease-in-out;
    }
    .${rangeId}__range::-moz-range-thumb:hover {
      background: ${indicatorColor};
    }
    .${rangeId}__range:active::-moz-range-thumb {
      background: ${indicatorColor};
    }
    .${rangeId}__range:focus::-webkit-slider-thumb {
      box-shadow: 0 0 0 3px #fff, 0 0 0 6px ${indicatorColor};
    }
    .${rangeId}__value {
      display: inline-block;
      position: relative;
      width: 60px;
      color: #fff;
      line-height: 20px;
      text-align: center;
      border-radius: 0.375rem;
      background: ${indicatorColor};
      padding: 5px 10px;
    }
    ::-moz-range-track {
      background: rgba(229, 231, 235, var(--tw-text-opacity));
      border: 0;
    }
    input::-moz-focus-inner,
    input::-moz-focus-outer {
      border: 0;
    }
		.${rangeId}__range::-webkit-slider-thumb {
			appearance: none;
			width: 20px;
			height: 20px;
			border-radius: 50%;
			background: ${indicatorColor};
			cursor: pointer;
			transition: background 0.15s ease-in-out;
		}
		.${rangeId}__range::-moz-range-thumb {
			width: 20px;
			height: 20px;
			border: 0;
			border-radius: 50%;
			background: ${indicatorColor};
			cursor: pointer;
			transition: background 0.15s ease-in-out;
		}
		.${rangeId}__range::-moz-range-thumb:hover {
			background: ${indicatorColor};
		}
		.${rangeId}__range:active::-moz-range-thumb {
			background: ${indicatorColor};
		}
		.${rangeId}__range::-webkit-slider-thumb {
			box-shadow: 0 0 0 3px #fff, 0 0 0 6px ${indicatorColor};
		}
		.${rangeId}__value {
			display: inline-block;
			position: relative;
			width: 60px;
			color: #fff;
			line-height: 20px;
			text-align: center;
			border-radius: 0.375rem;
			background: ${indicatorColor};
			padding: 5px 10px;
		}
		`;
    document.body.appendChild(sheet);

    return () => {
      const sheetToBeRemoved = document.getElementById(`slider-styles-${rangeId}`);
      const sheetParent = sheetToBeRemoved?.parentNode;

      if (sheetParent) {
        sheetParent.removeChild(sheetToBeRemoved);
      }
    };
  }, [indicatorColor, rangeId, grayColor]);

  const onChangeSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const newValue = Number(e.target.value);
    onChange({
      value: newValue,
      pain,
    });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;

    setClickStartedValue(Number(e.currentTarget.value));
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;

    const newValue = Number(e.currentTarget.value);

    if (!isDragging) {
      if (newValue === preferred) {
        // If clicked value is preferred and valueSet is false, just set to preferred
        if (value === preferred && !valueSet) {
          onChange({ value: preferred, pain });
        } else {
          // Toggle to undefined if the preferred value is clicked again and valueSet is true
          onChange({
            value: value === preferred ? undefined : preferred,
            pain,
          });
        }
      } else if (newValue === value && value === clickStartedValue && valueSet) {
        // Toggle to undefined if the same value is clicked and valueSet is true
        onChange({ value: undefined, pain });
      } else {
        // Set to the clicked value if it's a new value
        onChange({ value: newValue, pain });
      }
    } else {
      // Handle dragging case
      onChange({ value: newValue, pain });
    }

    setIsDragging(false);
    setClickStartedValue(null);
  };

  const numberOfSteps = Math.floor((max - min) / step);
  const percentForEachStep = 100 / numberOfSteps;
  const percentForPreferred = (Math.abs(min - (preferred || 0)) / step) * percentForEachStep;
  const percentForValue = (Math.abs(min - (value || 0)) / step) * percentForEachStep;

  return (
    <div className="h-16 flex flex-row gap-4">
      <div className="grow">
        <div className="w-full py-2 text-xs text-gray-500 sm:text-sm md:text-md relative">
          <div className="relative h-4 z-10" style={{ marginLeft: 10, width: 'calc(100% - 20px)' }}>
            <div
              className={`absolute ${showTopLabels ? 'text-center' : 'w-6 text-center'}`}
              style={{
                left: -11.5,
                borderRadius: 8,
                zIndex: showTopLabels ? 1 : -1,
                top: showTopLabels ? -14 : 4,
                whiteSpace: showTopLabels ? 'nowrap' : undefined,
              }}
            >
              {left}
            </div>
            {typeof preferred === 'number' && (!valueSet || value !== preferred) && (
              <div
                className="absolute w-6 text-center"
                style={{
                  marginLeft: -11.5,
                  left: `${percentForPreferred}%`,
                  borderRadius: 8,
                  zIndex: -1,
                  top: 4,
                }}
              >
                {preferred}
              </div>
            )}
            <div
              className={`absolute ${showTopLabels ? 'text-center' : 'w-6 text-center'}`}
              style={{
                right: -11.5,
                borderRadius: 8,
                zIndex: showTopLabels ? 1 : -1,
                top: showTopLabels ? -14 : 4,
                whiteSpace: showTopLabels ? 'nowrap' : undefined,
              }}
            >
              {right}
            </div>
            {!!valueSet && (
              <div
                className="absolute w-10 text-center text-white"
                style={{
                  marginLeft: -19.5,
                  left: `${percentForValue}%`,
                  borderRadius: 8,
                  zIndex: -1,
                  top: 4,
                  fontWeight: 'bold',
                  backgroundColor: indicatorColor,
                }}
              >
                {value}
              </div>
            )}
          </div>
        </div>
        <div className="relative w-full rounded-full z-10">
          <div className={rangeId}>
            <div className="relative" style={{ marginLeft: 10, marginRight: 10 }}>
              <div
                className="bg-gray-300 dark:bg-dark-gray-600 absolute"
                style={{ left: -2.5, width: 5, height: 25, borderRadius: 8, zIndex: -1, top: -4 }}
              />
              <div
                className="bg-gray-300 dark:bg-dark-gray-600 absolute"
                style={{ right: -2.5, width: 5, height: 25, borderRadius: 8, zIndex: -1, top: -4 }}
              />
              {!!(typeof preferred === 'number') && (
                <div
                  className="bg-gray-300 dark:bg-dark-gray-600 absolute"
                  style={{
                    marginLeft: -2.5,
                    left: `${percentForPreferred}%`,
                    width: 5,
                    height: 25,
                    borderRadius: 8,
                    zIndex: -1,
                    top: -4,
                  }}
                />
              )}
            </div>
            <input
              className={`${rangeId}__range`}
              type="range"
              value={value}
              min={min}
              max={max}
              step={step}
              onChange={onChangeSlider}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
